import React, { memo, useMemo, useState, useEffect, useCallback } from 'react';
import { FlatList, StyleSheet } from 'react-native';
import { selectAllSavingAccounts, selectAllCurrentAccounts, selectDefaultZeroAmount, selectAllInvestmentWithCryptoAccounts, } from 'reducers/selectors';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import { NETWORTH_PAGE_CONTROL_HEIGHT_WITH_PADDING } from 'features/accounts/values';
import { CardView, rem, SettingsItemNavigate } from 'design-system';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { formatPlural } from 'utils/formatting';
import { selectUnopenedInvestmentAccounts } from 'features/invest/reducers/selectors';
import { selectUnopenedPotsInfo } from 'features/pots/reducers/selectors';
import i18n from '../../../../i18n';
import { overviewColorMap, sections } from '../../constants';
import AccountsSectionCollapsible from './AccountSectionCollapsible';
import BankLoginsHelp from './BankLoginsHelp';
import { isWeb } from '../../../../constants';
function filterPositiveAccounts(data) {
    return Object.keys(data).reduce((acc, key) => {
        if (data[key].positiveAccounts.length > 0) {
            acc[key] =
                data[key];
        }
        return acc;
    }, {});
}
const NetWorthAssetsSection = ({ accounts, onRowPress, onOpenLogins, onAddAccount, openAlternative, onOpenUpgrade, onRowLongPress, initialTitle, isUnlockedForSpace, onContentSizeChange, }) => {
    const navigation = useMainStackNavigation();
    const colors = useColors();
    const [sectionShown, setSectionShown] = useState(initialTitle ? initialTitle === 'Net worth' : true);
    const unopenedPots = useAppSelector(selectUnopenedPotsInfo);
    const unopenedInvestmentAccounts = useAppSelector(selectUnopenedInvestmentAccounts);
    const filteredPositiveData = filterPositiveAccounts(accounts);
    useEffect(() => {
        if (!sectionShown) {
            const timeout = setTimeout(() => {
                setSectionShown(true);
            }, 400);
            return () => {
                clearTimeout(timeout);
            };
        }
        return () => { };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const zero = useAppSelector(selectDefaultZeroAmount);
    const allSavingsAccounts = useAppSelector(selectAllSavingAccounts);
    const allCurrentAccounts = useAppSelector(selectAllCurrentAccounts);
    const allInvestmentAccounts = useAppSelector(selectAllInvestmentWithCryptoAccounts);
    const data = useMemo(() => {
        if (!accounts || !sectionShown)
            return [];
        const baseData = [
            {
                firstCTA: `+ Add a ${i18n.t('current')} Account`,
                title: i18n.t('current'),
                data: allCurrentAccounts.positiveAccounts,
                total: {
                    amount: allCurrentAccounts.positiveTotal,
                    currency: allCurrentAccounts.currency,
                },
                color: colors.text[overviewColorMap.Checking],
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
            },
            {
                firstCTA: '+ Add Account',
                secondCTA: 'open pot',
                title: sections.savings,
                type: 'SAVINGS',
                data: unopenedPots.length
                    ? [...allSavingsAccounts.positiveAccounts, ...unopenedPots]
                    : allSavingsAccounts.positiveAccounts,
                total: {
                    amount: allSavingsAccounts.positiveTotal,
                    currency: allSavingsAccounts.currency,
                },
                color: colors.text[overviewColorMap.Savings],
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
                openAlternative: allSavingsAccounts.hasPots
                    ? undefined
                    : openAlternative,
            },
            {
                firstCTA: '+ Add investment',
                secondCTA: 'start in Emma',
                title: sections.investments,
                data: unopenedInvestmentAccounts.length
                    ? [
                        ...allInvestmentAccounts.positiveAccounts,
                        ...unopenedInvestmentAccounts,
                    ]
                    : allInvestmentAccounts.positiveAccounts,
                total: {
                    amount: allInvestmentAccounts.positiveTotal,
                    currency: allInvestmentAccounts.currency,
                },
                color: colors.text[overviewColorMap.Investments],
                type: 'INVESTMENT',
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
                openAlternative,
            },
            {
                firstCTA: '+ Add a pension',
                title: sections.pensions,
                data: accounts['PENSION'].positiveAccounts,
                total: {
                    amount: accounts['PENSION'].positiveTotal,
                    currency: accounts['PENSION'].array[0]?.currency ?? zero.currency,
                },
                color: colors.text.secondary,
                type: 'PENSION',
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
            },
            {
                firstCTA: '+ Add real estate',
                title: sections.realEstate,
                data: accounts['REAL_ESTATE'].positiveAccounts,
                total: {
                    amount: accounts['REAL_ESTATE'].positiveTotal,
                    currency: accounts['REAL_ESTATE'].array[0]?.currency ?? zero.currency,
                },
                color: colors.text.secondary,
                type: 'REAL_ESTATE',
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
            },
            {
                firstCTA: '+ Add a vehicle',
                title: sections.vehicles,
                data: accounts['VEHICLE'].positiveAccounts,
                total: {
                    amount: accounts['VEHICLE'].positiveTotal,
                    currency: accounts['VEHICLE'].array[0]?.currency ?? zero.currency,
                },
                color: colors.text.secondary,
                type: 'VEHICLE',
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
            },
            {
                firstCTA: '+ Add other assets',
                title: sections.otherAssets,
                data: accounts['OTHER'].positiveAccounts,
                total: {
                    amount: accounts['OTHER'].positiveTotal,
                    currency: accounts['OTHER'].array[0]?.currency ?? zero.currency,
                },
                color: colors.text.secondary,
                type: 'OTHER',
                onRowPress,
                onRowLongPress,
                onOpenLogins,
                onOpenUpgrade,
                onAddAccount,
            },
            ...('LOAN' in filteredPositiveData
                ? [
                    {
                        title: sections.loans,
                        data: accounts['LOAN'].positiveAccounts,
                        total: {
                            amount: accounts['LOAN'].positiveTotal,
                            currency: accounts['LOAN'].array[0]?.currency ?? zero.currency,
                        },
                        color: colors.text[overviewColorMap.Investments],
                        onRowPress,
                        onRowLongPress,
                        onOpenLogins,
                        onOpenUpgrade,
                        onAddAccount,
                    },
                ]
                : []),
            ...('MORTGAGE' in filteredPositiveData
                ? [
                    {
                        title: sections.mortgages,
                        data: accounts['MORTGAGE'].positiveAccounts,
                        total: {
                            amount: accounts['MORTGAGE'].positiveTotal,
                            currency: accounts['MORTGAGE'].array[0]?.currency ?? zero.currency,
                        },
                        color: colors.text[overviewColorMap.Investments],
                        onRowPress,
                        onRowLongPress,
                        onOpenLogins,
                        onOpenUpgrade,
                        onAddAccount,
                    },
                ]
                : []),
            ...('CREDITCARD' in filteredPositiveData
                ? [
                    {
                        title: sections.creditCards,
                        data: accounts['CREDITCARD'].positiveAccounts,
                        total: {
                            amount: accounts['CREDITCARD'].positiveTotal,
                            currency: accounts['CREDITCARD'].array[0]?.currency ?? zero.currency,
                        },
                        color: colors.text[overviewColorMap.Investments],
                        onRowPress,
                        onRowLongPress,
                        onOpenLogins,
                        onOpenUpgrade,
                        onAddAccount,
                    },
                ]
                : []),
        ];
        return baseData;
    }, [
        accounts,
        allCurrentAccounts.currency,
        allCurrentAccounts.positiveAccounts,
        allCurrentAccounts.positiveTotal,
        allInvestmentAccounts.currency,
        allInvestmentAccounts.positiveAccounts,
        allInvestmentAccounts.positiveTotal,
        allSavingsAccounts.currency,
        allSavingsAccounts.hasPots,
        allSavingsAccounts.positiveAccounts,
        allSavingsAccounts.positiveTotal,
        colors.text,
        filteredPositiveData,
        onAddAccount,
        onOpenLogins,
        onOpenUpgrade,
        onRowLongPress,
        onRowPress,
        openAlternative,
        sectionShown,
        zero,
        unopenedInvestmentAccounts,
        unopenedPots,
    ]);
    const hiddenCount = accounts.HIDDEN.positiveAccounts.length;
    const closedCount = accounts.CLOSED.positiveAccounts.length;
    const onPressHidden = useCallback(() => navigation.navigate('HiddenAccountsOverview', { title: 'Assets' }), [navigation]);
    const onPressClosed = useCallback(() => navigation.navigate('ClosedAccountsOverview', { title: 'Assets' }), [navigation]);
    const renderItem = useCallback(({ item }) => (<AccountsSectionCollapsible {...item} blur={!isUnlockedForSpace}/>), [isUnlockedForSpace]);
    const ListFooter = useMemo(() => (<>
        {hiddenCount > 0 ? (<CardView noMarginTop>
            <SettingsItemNavigate onPress={onPressHidden} style={styles.webPadding} title={`${hiddenCount} ${formatPlural('hidden account', 'hidden accounts', hiddenCount)}`}/>
          </CardView>) : null}
        {closedCount > 0 ? (<CardView noMarginTop>
            <SettingsItemNavigate onPress={onPressClosed} style={styles.webPadding} title={`${closedCount} ${formatPlural('closed account', 'closed accounts', closedCount)}`}/>
          </CardView>) : null}
        <BankLoginsHelp />
      </>), [hiddenCount, onPressHidden, closedCount, onPressClosed]);
    return (<FlatList data={data} scrollEnabled={false} initialNumToRender={2} windowSize={2} renderItem={renderItem} ListFooterComponent={ListFooter} onContentSizeChange={onContentSizeChange} style={styles.container}/>);
};
export default memo(NetWorthAssetsSection);
const styles = StyleSheet.create({
    container: {
        ...(!isWeb && {
            marginTop: NETWORTH_PAGE_CONTROL_HEIGHT_WITH_PADDING,
        }),
    },
    webPadding: {
        ...(isWeb && {
            paddingHorizontal: rem(8),
        }),
    },
});
